import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { InteractionCategory } from "./InteractionDefinitions";
import { PickerCategoryScope } from "./useInteractionPickerList";

interface InteractionPickerRailProps {
  categories: InteractionCategory[];
  value: PickerCategoryScope;
  onChange: (scope: PickerCategoryScope) => void;
  getCategoryLabel: (category: InteractionCategory) => string;
}

export default function InteractionPickerRail(props: InteractionPickerRailProps) {
  const { categories, value, onChange, getCategoryLabel } = props;
  const { t } = useTranslation();

  const entries: { key: string; label: string; scope: PickerCategoryScope }[] = [
    { key: "all", label: t("interaction_picker_ui.all"), scope: null },
    ...categories.map((category) => ({
      key: category,
      label: getCategoryLabel(category),
      scope: category as PickerCategoryScope,
    })),
  ];

  // Deliberately no arrow-key handling: the arrows belong to the results, and
  // a second highlight moving on the same keys read as confusing. The rail is
  // pointer-driven; its buttons still take native Tab and Enter.
  return (
    <div
      role="tablist"
      aria-orientation="vertical"
      aria-label={t("interaction_picker_ui.categories_label") ?? ""}
      // Recessed against the results column, per the design: the rail and the
      // details pane sit on the page background, the results on the surface.
      className="w-[152px] shrink-0 border-r border-creator-border bg-creator-primary-background p-[8px] flex flex-col gap-[2px] overflow-y-auto pit-quiet-scrollbar"
    >
      {entries.map((entry) => (
        <button
          key={entry.key}
          type="button"
          role="tab"
          aria-selected={entry.scope === value}
          onClick={() => onChange(entry.scope)}
          className={classNames(
            "text-left px-[10px] py-[7px] rounded-[6px] text-[13px] transition-colors",
            entry.scope === value
              ? "bg-creator-primary-washed text-creator-text font-medium"
              : "text-creator-text-sub hover:bg-creator-childActive"
          )}
        >
          {entry.label}
        </button>
      ))}
    </div>
  );
}
